import React from 'react';
import type { Season } from '@sulis-liiga/common';
import { SeasonStatus } from '@sulis-liiga/common';

interface SeasonInfoProps {
  season: Season;
}

const SeasonInfo: React.FC<SeasonInfoProps> = ({ season }) => {
  const getStatusColor = (status: SeasonStatus) => {
    switch (status) {
      case SeasonStatus.ACTIVE:
        return 'bg-green-100 text-green-800';
      case SeasonStatus.UPCOMING:
        return 'bg-blue-100 text-blue-800';
      case SeasonStatus.COMPLETED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">{season.name}</h3>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(
              season.status,
            )}`}
          >
            {season.status}
          </span>
        </div>
      </div>
      <div className="border-t border-gray-200">
        <dl>
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Start Date</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {formatDate(season.startDate)}
            </dd>
          </div>
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">End Date</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {formatDate(season.endDate)}
            </dd>
          </div>
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Points Expiration</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {formatDate(season.pointsExpiration)}
            </dd>
          </div>
          {season.pointsConfig && (
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500">Points System</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                Win: {season.pointsConfig.win}, Loss: {season.pointsConfig.loss}
                {season.pointsConfig.draw !== undefined && `, Draw: ${season.pointsConfig.draw}`}
                {season.pointsConfig.walkover !== undefined &&
                  `, Walkover: ${season.pointsConfig.walkover}`}
                {season.pointsConfig.noShow !== undefined &&
                  `, No Show: ${season.pointsConfig.noShow}`}
              </dd>
            </div>
          )}
        </dl>
      </div>
    </div>
  );
};

export default SeasonInfo;
